import { useState, useRef, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
type IssueCategory = 'grammar' | 'spelling' | 'clarity' | 'engagement' | 'delivery' | 'ai_detected';

interface ProofIssue {
  id: string;
  category: IssueCategory;
  severity: 'critical' | 'warning' | 'suggestion';
  original: string;
  suggestion: string;
  explanation: string;
  startIndex: number;
  endIndex: number;
  fixed: boolean;
  dismissed: boolean;
}

interface AIDetectionResult {
  platform: string;
  icon: string;
  score: number; // 0–100, higher = more AI-like
  status: 'safe' | 'warning' | 'danger';
  color: string;
  bgColor: string;
  borderColor: string;
}

interface ScoreBreakdown {
  correctness: number;
  clarity: number;
  engagement: number;
  delivery: number;
}

// ─── Mock analysis engine ─────────────────────────────────────────────────────
const ISSUE_TEMPLATES: Omit<ProofIssue, 'id' | 'startIndex' | 'endIndex' | 'fixed' | 'dismissed'>[] = [
  {
    category: 'grammar',
    severity: 'critical',
    original: 'their is',
    suggestion: 'there is',
    explanation: 'Incorrect use of "their" (possessive). Use "there" to indicate existence.',
  },
  {
    category: 'spelling',
    severity: 'critical',
    original: 'recieve',
    suggestion: 'receive',
    explanation: 'Common misspelling. Remember: "i before e except after c".',
  },
  {
    category: 'clarity',
    severity: 'warning',
    original: 'in order to',
    suggestion: 'to',
    explanation: '"In order to" is wordy. Replace with simply "to" for cleaner prose.',
  },
  {
    category: 'clarity',
    severity: 'warning',
    original: 'due to the fact that',
    suggestion: 'because',
    explanation: 'This phrase is unnecessarily verbose. "Because" conveys the same meaning concisely.',
  },
  {
    category: 'engagement',
    severity: 'suggestion',
    original: 'very important',
    suggestion: 'critical / essential / pivotal',
    explanation: '"Very important" is vague. Use a stronger, more precise adjective.',
  },
  {
    category: 'delivery',
    severity: 'suggestion',
    original: 'it is worth noting that',
    suggestion: 'Notably, / Importantly,',
    explanation: 'This filler phrase weakens your opening. Start with the key point directly.',
  },
  {
    category: 'grammar',
    severity: 'critical',
    original: 'alot',
    suggestion: 'a lot',
    explanation: '"Alot" is not a word. The correct form is two words: "a lot".',
  },
  {
    category: 'ai_detected',
    severity: 'warning',
    original: 'It is important to note that',
    suggestion: 'Notably, / Worth highlighting:',
    explanation: 'This phrase is a strong AI writing signal. Rephrase to sound more natural.',
  },
  {
    category: 'ai_detected',
    severity: 'warning',
    original: 'In conclusion, it can be said that',
    suggestion: 'Ultimately, / To summarize,',
    explanation: 'Formulaic AI conclusion opener. Use a more distinctive closing phrase.',
  },
  {
    category: 'clarity',
    severity: 'warning',
    original: 'utilize',
    suggestion: 'use',
    explanation: '"Utilize" is often unnecessarily formal. "Use" is clearer and more direct.',
  },
  {
    category: 'engagement',
    severity: 'suggestion',
    original: 'things',
    suggestion: 'factors / elements / aspects',
    explanation: '"Things" is too vague for academic writing. Use a more specific noun.',
  },
  {
    category: 'delivery',
    severity: 'suggestion',
    original: 'As previously mentioned',
    suggestion: 'As noted above / As discussed in Section 2',
    explanation: 'Vague back-reference. Point to the specific section for clarity.',
  },
];

const SAMPLE_TEXT = `The impact of artificial intelligence on modern education is a topic that has garnered significant attention in recent years. It is important to note that their is a growing body of research suggesting that AI tools can enhance learning outcomes. In order to fully understand this phenomenon, we must first examine the existing literature on educational technology.

Many researchers have argued that the integration of AI in classrooms can be very important for student engagement. Due to the fact that students today are digital natives, they tend to recieve information more effectively through interactive platforms. Alot of studies have shown that personalized learning algorithms can adapt to individual student needs.

In conclusion, it can be said that AI represents a transformative force in education. Educators should utilize these tools strategically to maximize their pedagogical impact. As previously mentioned, the key things to consider are accessibility, equity, and pedagogical alignment.`;

function generateIssues(text: string): ProofIssue[] {
  const issues: ProofIssue[] = [];
  ISSUE_TEMPLATES.forEach((template, idx) => {
    const lowerText = text.toLowerCase();
    const lowerOriginal = template.original.toLowerCase();
    const pos = lowerText.indexOf(lowerOriginal);
    if (pos !== -1) {
      issues.push({
        ...template,
        id: `issue-${idx}`,
        startIndex: pos,
        endIndex: pos + template.original.length,
        fixed: false,
        dismissed: false,
      });
    }
  });
  return issues;
}

function computeScores(issues: ProofIssue[]): ScoreBreakdown {
  const active = issues.filter(i => !i.fixed && !i.dismissed);
  const grammar = active.filter(i => i.category === 'grammar' || i.category === 'spelling').length;
  const clarity = active.filter(i => i.category === 'clarity').length;
  const engagement = active.filter(i => i.category === 'engagement').length;
  const delivery = active.filter(i => i.category === 'delivery' || i.category === 'ai_detected').length;
  return {
    correctness: Math.max(0, 100 - grammar * 18),
    clarity: Math.max(0, 100 - clarity * 14),
    engagement: Math.max(0, 100 - engagement * 12),
    delivery: Math.max(0, 100 - delivery * 10),
  };
}

function computeAIDetection(issues: ProofIssue[]): AIDetectionResult[] {
  const aiIssues = issues.filter(i => i.category === 'ai_detected' && !i.fixed && !i.dismissed).length;
  const baseScore = Math.min(95, 30 + aiIssues * 22);
  return [
    { platform: 'Turnitin', icon: 'ri-shield-check-line', score: Math.min(95, baseScore + 5), status: baseScore > 60 ? 'danger' : baseScore > 35 ? 'warning' : 'safe', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
    { platform: 'GPTZero', icon: 'ri-robot-line', score: Math.min(95, baseScore - 3), status: baseScore > 55 ? 'danger' : baseScore > 30 ? 'warning' : 'safe', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
    { platform: 'Copyleaks', icon: 'ri-file-search-line', score: Math.min(95, baseScore + 8), status: baseScore > 58 ? 'danger' : baseScore > 33 ? 'warning' : 'safe', color: 'text-teal-600', bgColor: 'bg-teal-50', borderColor: 'border-teal-200' },
    { platform: 'Originality.ai', icon: 'ri-eye-line', score: Math.min(95, baseScore - 6), status: baseScore > 52 ? 'danger' : baseScore > 28 ? 'warning' : 'safe', color: 'text-violet-600', bgColor: 'bg-violet-50', borderColor: 'border-violet-200' },
  ];
}

// ─── Category config ──────────────────────────────────────────────────────────
const CATEGORY_CONFIG: Record<IssueCategory, { label: string; icon: string; color: string; bg: string; border: string; highlight: string }> = {
  grammar:      { label: 'Grammar',      icon: 'ri-error-warning-line',  color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200',    highlight: 'bg-red-100 border-b-2 border-red-400' },
  spelling:     { label: 'Spelling',     icon: 'ri-font-size',           color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-200',   highlight: 'bg-rose-100 border-b-2 border-rose-400' },
  clarity:      { label: 'Clarity',      icon: 'ri-focus-3-line',        color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200',  highlight: 'bg-amber-100 border-b-2 border-amber-400' },
  engagement:   { label: 'Engagement',   icon: 'ri-fire-line',           color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', highlight: 'bg-orange-100 border-b-2 border-orange-400' },
  delivery:     { label: 'Delivery',     icon: 'ri-send-plane-line',     color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', highlight: 'bg-indigo-100 border-b-2 border-indigo-400' },
  ai_detected:  { label: 'AI Signal',    icon: 'ri-robot-line',          color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', highlight: 'bg-violet-100 border-b-2 border-violet-400' },
};

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 80 }: { score: number; size?: number }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f3f4f6" strokeWidth={8} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={8}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 1s ease' }}
      />
      <text
        x={size / 2} y={size / 2}
        textAnchor="middle" dominantBaseline="central"
        className="rotate-90"
        style={{ transform: `rotate(90deg) translate(0px, -${size}px)`, fontSize: size * 0.22, fontWeight: 800, fill: color }}
      >
        {score}
      </text>
    </svg>
  );
}

// ─── Highlighted Text ─────────────────────────────────────────────────────────
function HighlightedText({ text, issues, activeIssueId, onIssueClick }: {
  text: string;
  issues: ProofIssue[];
  activeIssueId: string | null;
  onIssueClick: (id: string) => void;
}) {
  const activeIssues = issues.filter(i => !i.fixed && !i.dismissed);
  if (activeIssues.length === 0) return <span className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{text}</span>;

  // Build segments
  const sorted = [...activeIssues].sort((a, b) => a.startIndex - b.startIndex);
  const segments: { text: string; issue?: ProofIssue }[] = [];
  let cursor = 0;
  for (const issue of sorted) {
    if (issue.startIndex > cursor) {
      segments.push({ text: text.slice(cursor, issue.startIndex) });
    }
    segments.push({ text: text.slice(issue.startIndex, issue.endIndex), issue });
    cursor = issue.endIndex;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor) });

  return (
    <span className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
      {segments.map((seg, i) => {
        if (!seg.issue) return <span key={i}>{seg.text}</span>;
        const cfg = CATEGORY_CONFIG[seg.issue.category];
        const isActive = activeIssueId === seg.issue.id;
        return (
          <span
            key={i}
            onClick={() => onIssueClick(seg.issue!.id)}
            title={seg.issue.explanation}
            className={`cursor-pointer rounded px-0.5 transition-all ${cfg.highlight} ${isActive ? 'ring-2 ring-offset-1 ring-purple-400' : 'hover:opacity-80'}`}
          >
            {seg.text}
          </span>
        );
      })}
    </span>
  );
}

// ─── Issue Card ───────────────────────────────────────────────────────────────
function IssueCard({ issue, isActive, onFix, onDismiss, onClick }: {
  issue: ProofIssue;
  isActive: boolean;
  onFix: (id: string) => void;
  onDismiss: (id: string) => void;
  onClick: (id: string) => void;
}) {
  const cfg = CATEGORY_CONFIG[issue.category];
  const severityBadge = issue.severity === 'critical'
    ? 'bg-red-100 text-red-700'
    : issue.severity === 'warning'
    ? 'bg-amber-100 text-amber-700'
    : 'bg-gray-100 text-gray-600';

  return (
    <div
      onClick={() => onClick(issue.id)}
      className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${isActive ? `${cfg.border} ${cfg.bg} shadow-md` : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.bg}`}>
          <i className={`${cfg.icon} ${cfg.color} text-sm`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${severityBadge}`}>
              {issue.severity === 'critical' ? 'Critical' : issue.severity === 'warning' ? 'Warning' : 'Suggestion'}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-xs font-mono bg-red-50 text-red-700 px-2 py-0.5 rounded line-through">{issue.original}</span>
            <i className="ri-arrow-right-line text-gray-400 text-xs flex-shrink-0"></i>
            <span className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-bold">{issue.suggestion}</span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">{issue.explanation}</p>
        </div>
      </div>
      {isActive && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); onFix(issue.id); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-xl hover:shadow-md transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-check-line text-sm"></i>
            Apply Fix
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(issue.id); }}
            className="flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-500 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main ProofreadPanel ──────────────────────────────────────────────────────
export default function ProofreadPanel() {
  const [inputText, setInputText] = useState(SAMPLE_TEXT);
  const [issues, setIssues] = useState<ProofIssue[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<IssueCategory | 'all'>('all');
  const [isFixingAll, setIsFixingAll] = useState(false);
  const [fixAllStep, setFixAllStep] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ANALYSIS_STEPS = [
    'Tokenizing and parsing text structure…',
    'Running grammar & spelling checks…',
    'Evaluating sentence clarity & conciseness…',
    'Scoring engagement and vocabulary strength…',
    'Detecting AI writing patterns & signals…',
    'Calculating AI detection risk scores…',
    'Generating fix suggestions…',
    'Analysis complete ✅',
  ];

  const handleAnalyze = useCallback(() => {
    if (!inputText.trim()) return;
    setIsAnalyzing(true);
    setHasAnalyzed(false);
    setIssues([]);
    setActiveIssueId(null);
    setAnalysisStep(0);
    let step = 0;
    intervalRef.current = setInterval(() => {
      step++;
      setAnalysisStep(step);
      if (step >= ANALYSIS_STEPS.length - 1) {
        clearInterval(intervalRef.current!);
        const found = generateIssues(inputText);
        setIssues(found);
        setIsAnalyzing(false);
        setHasAnalyzed(true);
      }
    }, 500);
  }, [inputText]);

  const handleFix = useCallback((id: string) => {
    setIssues(prev => prev.map(i => i.id === id ? { ...i, fixed: true } : i));
    setActiveIssueId(null);
  }, []);

  const handleDismiss = useCallback((id: string) => {
    setIssues(prev => prev.map(i => i.id === id ? { ...i, dismissed: true } : i));
    setActiveIssueId(null);
  }, []);

  const handleFixAll = useCallback(() => {
    const activeIssues = issues.filter(i => !i.fixed && !i.dismissed);
    if (activeIssues.length === 0) return;
    setIsFixingAll(true);
    setFixAllStep(0);
    let step = 0;
    const tick = setInterval(() => {
      if (step < activeIssues.length) {
        const id = activeIssues[step].id;
        setIssues(prev => prev.map(i => i.id === id ? { ...i, fixed: true } : i));
        setFixAllStep(step + 1);
        step++;
      } else {
        clearInterval(tick);
        setIsFixingAll(false);
        setActiveIssueId(null);
      }
    }, 300);
  }, [issues]);

  const activeIssues = issues.filter(i => !i.fixed && !i.dismissed);
  const fixedCount = issues.filter(i => i.fixed).length;
  const filteredIssues = filterCategory === 'all'
    ? activeIssues
    : activeIssues.filter(i => i.category === filterCategory);

  const scores = computeScores(issues);
  const overallScore = hasAnalyzed
    ? Math.round((scores.correctness + scores.clarity + scores.engagement + scores.delivery) / 4)
    : 0;
  const aiDetection = computeAIDetection(issues);

  const categoryGroups = (['grammar', 'spelling', 'clarity', 'engagement', 'delivery', 'ai_detected'] as IssueCategory[]).map(cat => ({
    cat,
    count: activeIssues.filter(i => i.category === cat).length,
  })).filter(g => g.count > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex-shrink-0">
          <i className="ri-spell-check-line text-white text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">Smart Proofreader</p>
          <p className="text-xs text-gray-500">Grammar · Clarity · Engagement · AI Detection Risk</p>
        </div>
        {hasAnalyzed && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${overallScore >= 80 ? 'bg-emerald-100 text-emerald-700' : overallScore >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
              Score: {overallScore}/100
            </span>
          </div>
        )}
      </div>

      {/* Text Input */}
      <div className="rounded-2xl border-2 border-gray-200 overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-bold text-gray-600 flex items-center gap-2">
            <i className="ri-file-text-line text-purple-500"></i>
            Your Text
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{inputText.length} characters</span>
            {hasAnalyzed && (
              <button
                onClick={() => { setHasAnalyzed(false); setIssues([]); setActiveIssueId(null); }}
                className="text-xs text-purple-600 font-semibold hover:text-purple-800 cursor-pointer whitespace-nowrap"
              >
                Edit text
              </button>
            )}
          </div>
        </div>

        {!hasAnalyzed ? (
          <textarea
            value={inputText}
            onChange={(e) => { if (e.target.value.length <= 2500) setInputText(e.target.value); }}
            placeholder="Paste your paper text here to proofread it…"
            rows={10}
            className="w-full px-5 py-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none resize-none leading-relaxed"
          />
        ) : (
          <div className="px-5 py-4 min-h-[200px] leading-relaxed">
            <HighlightedText
              text={inputText}
              issues={issues}
              activeIssueId={activeIssueId}
              onIssueClick={setActiveIssueId}
            />
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2">
            {hasAnalyzed && activeIssues.length > 0 && (
              <button
                onClick={handleFixAll}
                disabled={isFixingAll}
                className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-full hover:shadow-md transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {isFixingAll ? (
                  <><i className="ri-loader-4-line animate-spin text-xs"></i> Fixing {fixAllStep}/{activeIssues.length}…</>
                ) : (
                  <><i className="ri-magic-line text-xs"></i> Auto-Fix All ({activeIssues.length})</>
                )}
              </button>
            )}
            {hasAnalyzed && fixedCount > 0 && (
              <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-sm"></i>
                {fixedCount} fixed
              </span>
            )}
          </div>
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !inputText.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <><i className="ri-loader-4-line animate-spin text-base"></i> Analyzing…</>
            ) : (
              <><i className="ri-spell-check-2-line text-base"></i> {hasAnalyzed ? 'Re-Analyze' : 'Analyze Text'}</>
            )}
          </button>
        </div>
      </div>

      {/* Analysis Progress */}
      {isAnalyzing && (
        <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 p-5">
          <p className="text-sm font-bold text-purple-800 mb-4 flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin text-purple-500"></i>
            Running Analysis…
          </p>
          <div className="space-y-2">
            {ANALYSIS_STEPS.map((step, i) => (
              <div key={i} className={`flex items-center gap-3 transition-all duration-300 ${i <= analysisStep ? 'opacity-100' : 'opacity-30'}`}>
                <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${i < analysisStep ? 'bg-emerald-100' : i === analysisStep ? 'bg-purple-100' : 'bg-gray-100'}`}>
                  {i < analysisStep ? (
                    <i className="ri-check-line text-emerald-500 text-xs"></i>
                  ) : i === analysisStep ? (
                    <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                  ) : null}
                </div>
                <span className={`text-xs font-medium ${i <= analysisStep ? 'text-gray-700' : 'text-gray-400'}`}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {hasAnalyzed && (
        <div className="space-y-6">
          {/* Score Dashboard */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Overall */}
            <div className="md:col-span-1 flex flex-col items-center justify-center p-4 rounded-2xl bg-white border-2 border-gray-100 shadow-sm">
              <ScoreRing score={overallScore} size={72} />
              <p className="text-xs font-bold text-gray-600 mt-2 text-center">Overall Score</p>
              <p className={`text-xs font-semibold mt-0.5 ${overallScore >= 80 ? 'text-emerald-600' : overallScore >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                {overallScore >= 80 ? 'Excellent' : overallScore >= 60 ? 'Good' : 'Needs Work'}
              </p>
            </div>

            {/* Sub-scores */}
            {[
              { label: 'Correctness', value: scores.correctness, icon: 'ri-check-double-line', color: 'text-red-500' },
              { label: 'Clarity', value: scores.clarity, icon: 'ri-focus-3-line', color: 'text-amber-500' },
              { label: 'Engagement', value: scores.engagement, icon: 'ri-fire-line', color: 'text-orange-500' },
              { label: 'Delivery', value: scores.delivery, icon: 'ri-send-plane-line', color: 'text-indigo-500' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center justify-center p-4 rounded-2xl bg-white border-2 border-gray-100 shadow-sm">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 mb-2">
                  <i className={`${s.icon} ${s.color} text-base`}></i>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${s.value >= 80 ? 'bg-emerald-500' : s.value >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${s.value}%` }}
                  />
                </div>
                <p className="text-sm font-extrabold text-gray-800">{s.value}</p>
                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
              </div>
            ))}
          </div>

          {/* AI Detection Risk */}
          <div className="rounded-2xl border-2 border-violet-100 bg-gradient-to-br from-violet-50 to-purple-50 p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
                <i className="ri-radar-line text-violet-500"></i>
                AI Detection Risk
              </h4>
              <span className="text-xs text-gray-500 font-medium">Lower score = safer</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {aiDetection.map((d) => (
                <div key={d.platform} className={`rounded-xl border ${d.borderColor} ${d.bgColor} p-3 text-center`}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white mx-auto mb-2 shadow-sm">
                    <i className={`${d.icon} ${d.color} text-base`}></i>
                  </div>
                  <p className="text-xs font-bold text-gray-700 mb-1">{d.platform}</p>
                  <div className="w-full bg-white/60 rounded-full h-1.5 mb-1.5">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${d.status === 'danger' ? 'bg-red-500' : d.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${d.score}%` }}
                    />
                  </div>
                  <p className={`text-sm font-extrabold ${d.status === 'danger' ? 'text-red-600' : d.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {d.score}%
                  </p>
                  <p className={`text-xs font-semibold ${d.status === 'danger' ? 'text-red-500' : d.status === 'warning' ? 'text-amber-500' : 'text-emerald-500'}`}>
                    {d.status === 'danger' ? '⚠ High Risk' : d.status === 'warning' ? '~ Moderate' : '✓ Safe'}
                  </p>
                </div>
              ))}
            </div>
            {activeIssues.some(i => i.category === 'ai_detected') && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-amber-700 leading-relaxed">
                  <strong>AI signals detected.</strong> Fix the highlighted "AI Signal" issues below to lower your detection risk scores automatically.
                </p>
              </div>
            )}
          </div>

          {/* Issues List */}
          {activeIssues.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
                  <i className="ri-list-check-2 text-purple-500"></i>
                  Issues Found
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{activeIssues.length}</span>
                </h4>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => setFilterCategory('all')}
                    className={`px-3 py-1 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${filterCategory === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  >
                    All ({activeIssues.length})
                  </button>
                  {categoryGroups.map(g => {
                    const cfg = CATEGORY_CONFIG[g.cat];
                    return (
                      <button
                        key={g.cat}
                        onClick={() => setFilterCategory(g.cat)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${filterCategory === g.cat ? `${cfg.bg} ${cfg.color} ${cfg.border}` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                      >
                        <i className={`${cfg.icon} text-xs`}></i>
                        {cfg.label} ({g.count})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {filteredIssues.map(issue => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    isActive={activeIssueId === issue.id}
                    onFix={handleFix}
                    onDismiss={handleDismiss}
                    onClick={setActiveIssueId}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center rounded-2xl bg-emerald-50 border-2 border-emerald-200">
              <div className="w-16 h-16 flex items-center justify-center rounded-3xl bg-emerald-100 mb-4">
                <i className="ri-checkbox-circle-line text-emerald-500 text-3xl"></i>
              </div>
              <p className="text-base font-extrabold text-emerald-800 mb-1">All Issues Resolved!</p>
              <p className="text-sm text-emerald-600">Your text is clean, clear, and ready to use.</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasAnalyzed && !isAnalyzing && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
            <i className="ri-lightbulb-line text-amber-600 text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-amber-800 mb-1">How the Proofreader Works</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Paste any text above and click <strong>Analyze Text</strong>. The engine checks grammar, spelling, clarity, engagement, and delivery — just like Grammarly — then rates your AI detection risk across Turnitin, GPTZero, Copyleaks, and Originality.ai. Click any highlighted issue to see the fix, or use <strong>Auto-Fix All</strong> to resolve everything instantly.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
